import { useTheme } from "@mui/material";
import Button from "components/basic/button";
import GoogleRating, {
  GoogleRatingListing,
} from "components/basic/googleRating";
import VerifiedBadge from "components/basic/verifiedBadge";
import Typography from "components/basic/typography";

type QuoteActionCardButton = {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
};

export interface QuoteActionCardProps {
  companyName: string;
  googleListing: GoogleRatingListing | null;
  isVerified: boolean;
  distanceText: string;
  responseText: string;
  registrationLabel: string;
  registrationValue: string;
  insuranceLabel: string;
  insuranceValue: string;
  description: string;
  siteVisitAction: QuoteActionCardButton;
  // Optional "Book a Call" action, shown between the primary action and
  // "Message Vendor".
  bookVisitAction?: QuoteActionCardButton;
  messageVendorAction: QuoteActionCardButton;
  saveEstimateAction: QuoteActionCardButton;
}

const QuoteActionCard: React.FC<QuoteActionCardProps> = ({
  companyName,
  googleListing,
  isVerified,
  distanceText,
  responseText,
  registrationLabel,
  registrationValue,
  insuranceLabel,
  insuranceValue,
  description,
  siteVisitAction,
  bookVisitAction,
  messageVendorAction,
  saveEstimateAction,
}) => {
  const theme = useTheme();
  const quoteCardBackground = theme.palette.quoteCard.background;
  const textMuted45 = theme.palette.neutralAlpha.white45;

  const cardContainerStyle: React.CSSProperties = {
    backgroundColor: quoteCardBackground,
    border: `2px solid ${theme.palette.common.white}`,
    color: theme.palette.common.white,
    width: "100%",
    maxWidth: "440px",
    boxSizing: "border-box",
    padding: theme.spacing(2.2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.8),
    height: "fit-content",
  };

  const titleStyle: React.CSSProperties = {
    color: theme.palette.common.white,
    fontWeight: 700,
    margin: 0,
    lineHeight: 1.1,
    fontSize: "1.6em",
  };

  const detailsGridStyle: React.CSSProperties = {
    display: "grid",
    // Collapses to one column on narrow screens without a media query.
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: theme.spacing(2),
  };

  const detailsMutedItalicStyle: React.CSSProperties = {
    color: textMuted45,
    fontStyle: "italic",
    fontSize: "1rem",
  };

  const detailsMutedStyle: React.CSSProperties = {
    color: textMuted45,
    fontSize: "1rem",
    fontWeight: "bold",
  };

  const descriptionStyle: React.CSSProperties = {
    color: theme.palette.common.white,
    fontSize: "1rem",
    lineHeight: 1.35,
  };

  const actionsContainerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  };

  const actionButtonStyle: React.CSSProperties = {
    color: theme.palette.common.white,
    fontWeight: "bolder",
    fontSize: "1.2rem",
  };

  const primaryActionButtonStyle: React.CSSProperties = {
    ...actionButtonStyle,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  };

  const renderActionButton = (
    action: QuoteActionCardButton,
    color: "secondary" | "warning",
    style: React.CSSProperties,
  ) => (
    <Button
      variant="contained"
      color={color}
      size="small"
      fullWidth
      loading={action.loading}
      disabled={action.disabled}
      onClick={action.onClick}
      style={style}
    >
      {action.label}
    </Button>
  );

  return (
    <div style={cardContainerStyle}>
      <Typography variant="h4" style={titleStyle}>
        {companyName}
      </Typography>

      <VerifiedBadge verified={isVerified} tone="onDark" />
      <GoogleRating listing={googleListing} tone="onDark" />

      <div style={detailsGridStyle}>
        <Typography variant="body2" style={detailsMutedItalicStyle}>
          {distanceText}
        </Typography>
        <Typography variant="body2" style={detailsMutedItalicStyle}>
          {responseText}
        </Typography>

        <div>
          <Typography variant="body2" style={detailsMutedStyle}>
            {registrationLabel}
          </Typography>
          <Typography variant="body2" style={detailsMutedItalicStyle}>
            {registrationValue}
          </Typography>
        </div>

        <div>
          <Typography variant="body2" style={detailsMutedStyle}>
            {insuranceLabel}
          </Typography>
          <Typography variant="body2" style={detailsMutedItalicStyle}>
            {insuranceValue}
          </Typography>
        </div>
      </div>

      <Typography variant="body1" style={descriptionStyle}>
        {description}
      </Typography>

      <div style={actionsContainerStyle}>
        {renderActionButton(
          siteVisitAction,
          "secondary",
          primaryActionButtonStyle,
        )}
        {bookVisitAction &&
          renderActionButton(bookVisitAction, "warning", actionButtonStyle)}
        {renderActionButton(messageVendorAction, "warning", actionButtonStyle)}
        {renderActionButton(saveEstimateAction, "warning", actionButtonStyle)}
      </div>
    </div>
  );
};

export default QuoteActionCard;
